import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Gestion du consentement cookies — conforme nLPD (Suisse) + RGPD (UE).
 *
 * Catégories :
 *  • necessary  : verrouillée à true (cookies/storage strictement nécessaires
 *    au fonctionnement : authentification, langue, choix de consentement).
 *  • analytics  : OFF par défaut. GA4 (Sprint C2 — pas encore actif).
 *  • marketing  : OFF par défaut. Meta Pixel (Sprint C2 — pas encore actif).
 *
 * Stockage versionné dans localStorage. Si CONSENT_POLICY_VERSION change,
 * le consentement précédent est invalidé et la bannière réapparaît.
 */
export const CONSENT_POLICY_VERSION = 1;
const STORAGE_KEY = 'bodyup_consent_v1';

const DEFAULTS = Object.freeze({
  necessary: true,
  analytics: false,
  marketing: false,
});

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.policy_version !== CONSENT_POLICY_VERSION) return null; // ancien consentement invalidé
    return data;
  } catch {
    return null;
  }
}

function writeStored(consent) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...consent,
      necessary: true,
      policy_version: CONSENT_POLICY_VERSION,
      timestamp: new Date().toISOString(),
    }));
  } catch {/* silent */}
}

// Supprime les cookies tiers connus côté client (best-effort, sans accès aux flags HttpOnly).
function purgeTrackingCookies() {
  if (typeof document === 'undefined') return;
  const names = ['_ga', '_gid', '_gat', '_gcl_au', '_fbp', '_fbc'];
  const expire = 'Thu, 01 Jan 1970 00:00:00 GMT';
  const host = location.hostname;
  // Couvre tous les domaines/paths plausibles
  const domains = ['', host, '.' + host, host.replace(/^www\./, '.')].filter(Boolean);
  document.cookie.split(';').forEach(c => {
    const n = c.split('=')[0].trim();
    if (names.includes(n) || n.startsWith('_ga_')) {
      domains.forEach(d => {
        document.cookie = `${n}=; expires=${expire}; path=/${d ? `; domain=${d}` : ''}`;
      });
    }
  });
}

const ConsentContext = createContext({
  consent: DEFAULTS,
  hasDecided: false,
  isOpen: false,
  acceptAll: () => {},
  refuseAll: () => {},
  setPreferences: () => {},
  openPreferences: () => {},
  closePreferences: () => {},
  revoke: () => {},
});

export const ConsentProvider = ({ children }) => {
  const [consent, setConsentState] = useState(DEFAULTS);
  const [hasDecided, setHasDecided] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Bootstrap : lit le consentement précédent (s'il est encore à la version courante).
  useEffect(() => {
    const stored = readStored();
    if (stored) {
      setConsentState({
        necessary: true,
        analytics: !!stored.analytics,
        marketing: !!stored.marketing,
      });
      setHasDecided(true);
    }
  }, []);

  // Permet à d'autres composants (footer link) d'ouvrir la modale.
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('bodyup:open-consent', handler);
    return () => window.removeEventListener('bodyup:open-consent', handler);
  }, []);

  const persist = useCallback((next) => {
    const merged = { necessary: true, analytics: !!next.analytics, marketing: !!next.marketing };
    setConsentState(merged);
    setHasDecided(true);
    writeStored(merged);
    // Si l'utilisateur retire un consentement, purge immédiate des cookies tiers correspondants.
    if (!merged.analytics || !merged.marketing) purgeTrackingCookies();
    // Notifie les autres composants (utile pour Sprint C2 : charger/décharger GA4/Meta)
    window.dispatchEvent(new CustomEvent('bodyup:consent-changed', { detail: merged }));
  }, []);

  const acceptAll = useCallback(() => {
    persist({ analytics: true, marketing: true });
    setIsOpen(false);
  }, [persist]);

  const refuseAll = useCallback(() => {
    persist({ analytics: false, marketing: false });
    setIsOpen(false);
  }, [persist]);

  const setPreferences = useCallback((prefs) => {
    persist(prefs);
    setIsOpen(false);
  }, [persist]);

  const openPreferences = useCallback(() => setIsOpen(true), []);
  const closePreferences = useCallback(() => setIsOpen(false), []);

  const revoke = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {/* silent */}
    setConsentState(DEFAULTS);
    setHasDecided(false);
    purgeTrackingCookies();
    window.dispatchEvent(new CustomEvent('bodyup:consent-changed', { detail: DEFAULTS }));
  }, []);

  const value = useMemo(() => ({
    consent, hasDecided, isOpen,
    acceptAll, refuseAll, setPreferences, openPreferences, closePreferences, revoke,
  }), [consent, hasDecided, isOpen, acceptAll, refuseAll, setPreferences, openPreferences, closePreferences, revoke]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => useContext(ConsentContext);
